import { type VariantProps, cva } from "class-variance-authority";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";

const baseClasses = [
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  "relative isolate overflow-hidden transition duration-300 ease-[cubic-bezier(0.4,0.36,0,1)]",
  "before:duration-300 before:ease-[cubic-bezier(0.4,0.36,0,1)] before:transtion-opacity before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:rounded-md before:bg-gradient-to-b before:from-white/20 before:opacity-50 hover:before:opacity-80 dark:before:opacity-0",
  "after:pointer-events-none after:absolute after:inset-0 after:-z-10 after:rounded-md after:bg-gradient-to-b after:from-white/10 after:from-[35%] after:to-[70%] after:mix-blend-overlay dark:after:opacity-0",
  "aria-[invalid=true]:ring-destructive",
];

const buttonVariants = cva(baseClasses.join(" "), {
  variants: {
    variant: {
      default: "bg-primary text-white ring-1 ring-primary",
      destructive:
        "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      outline:
        "ring-1 ring-input bg-background dark:text-secondary hover:bg-accent hover:text-accent-foreground",
      secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
      ghost:
        "text-muted hover:bg-accent hover:text-accent-foreground bg-transparent border border-transparent",
      link: "text-primary underline-offset-4 hover:underline before:hidden after:hidden rounded-none",
    },
    size: {
      default: "h-9 px-4 py-2",
      xs: "h-auto rounded-md px-3",
      lg: "h-11 rounded-md px-8",
      icon: "h-9 w-9",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isAnimated?: boolean;
}

const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & { isLoading?: boolean }
>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      isAnimated = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        disabled={isLoading}
        ref={ref}
        {...props}
      >
        {asChild || !isAnimated ? (
          children
        ) : (
          <>
            <AnimatePresence mode="wait">
              <motion.span
                key="loader"
                initial={{ opacity: isLoading ? 0 : 1, y: isLoading ? -20 : 0 }}
                animate={{ opacity: isLoading ? 1 : 0, y: isLoading ? 0 : -20 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.2 }}
                className="absolute"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
              </motion.span>
            </AnimatePresence>

            {React.Children.map(children, (child) => (
              <motion.div
                key="content"
                initial={{ opacity: isLoading ? 0 : 1, y: isLoading ? 20 : 0 }}
                animate={{ opacity: isLoading ? 0 : 1, y: isLoading ? 20 : 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {child}
              </motion.div>
            ))}
            {/* </motion.span> */}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
